import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Datastore } from '@freelancer/datastore';
import {
  ReviewSkill,
  ReviewsSkillsCollection,
} from '@freelancer/datastore/collections';
import { Margin } from '@freelancer/ui/margin';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor, FontType } from '@freelancer/ui/text';
import { RoleApi } from 'api-typings/common/common';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { SkillFilterItem } from '../user-profile-reviews.component';

@Component({
  selector: 'app-user-profile-reviews-filter',
  template: `
    <fl-bit
      *ngIf="selectedSkillFilters$ | flAsync as selectedSkillFilters"
      class="ReviewFilter"
    >
      <fl-text
        i18n="Profile review filter reviews text"
        [fontType]="FontType.SPAN"
        [flMarginRight]="Margin.SMALL"
      >
        Filter reviews by:
      </fl-text>

      <fl-dropdown-filter
        buttonText="View All"
        i18n-buttonText="Profile reviews skill filter"
        buttonTextApplied="{{ selectedSkillFilters.length }} skill(s) selected"
        i18n-buttonTextApplied="Profile review skill filter"
        [edgeToEdge]="true"
        [filterApplied]="selectedSkillFilters.length > 0"
      >
        <fl-bit class="DropdownContainer">
          <fl-bit class="ContentContainer">
            <fl-bit [flMarginBottom]="Margin.SMALL">
              <fl-checkbox
                i18n="View all review filter option"
                flTrackingLabel="ReviewViewAllOption"
                i18n-label="Profile review filter label"
                label="View All"
                [control]="viewAllFilterControl"
                (click)="handleResetFilters()"
              ></fl-checkbox>
            </fl-bit>

            <fl-text
              i18n="Sub menu text for filter"
              [color]="FontColor.MID"
              [flMarginBottom]="Margin.SMALL"
            >
              Skills
            </fl-text>
            <fl-bit
              [flMarginBottom]="
                (showBottomFade$ | flAsync) ? Margin.XSMALL : Margin.NONE
              "
            >
              <fl-bit
                *ngFor="
                  let name of filterControlItems$ | flAsync;
                  let last = last;
                  trackBy: trackBySkillFilterItem
                "
              >
                <fl-checkbox
                  *ngIf="filterGroup?.get(name.controlName)"
                  class="CheckboxContainer"
                  flTrackingLabel="ReviewSortOption{{
                    name.controlName | pascalCase
                  }}"
                  [control]="filterGroup?.get(name.controlName)"
                  [label]="name.displayName"
                  [flMarginRight]="Margin.SMALL"
                  [flMarginBottom]="last ? Margin.NONE : Margin.SMALL"
                ></fl-checkbox>
              </fl-bit>
            </fl-bit>
          </fl-bit>
        </fl-bit>
        <fl-bit
          *ngIf="showBottomFade$ | flAsync"
          class="BottomFadeToWhite"
        ></fl-bit>
      </fl-dropdown-filter>
    </fl-bit>
  `,
  styleUrls: ['./user-profile-reviews-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileReviewsFilterComponent implements OnInit, OnDestroy {
  FontType = FontType;
  Margin = Margin;
  FontColor = FontColor;
  SpinnerSize = SpinnerSize;

  @Input() usersId$: Rx.Observable<number>;
  @Input() viewProfileAs$: Rx.Observable<RoleApi>;

  @Output() filtersSelected = new EventEmitter<readonly ReviewSkill[]>();
  @Output() scrolledToBottom = new EventEmitter<void>();

  private filterGroupSubscription?: Rx.Subscription;
  private filterGroupSetupSubscription?: Rx.Subscription;
  private profileViewAsSubscription?: Rx.Subscription;

  private readonly FILTER_CONTROLS_DISPLAY_COUNT = 5;
  private reviewSkills$: Rx.Observable<readonly ReviewSkill[]>;
  private _selectedSkillFilters$ = new Rx.BehaviorSubject<
    readonly ReviewSkill[]
  >([]);

  filterControlItems$: Rx.Observable<readonly SkillFilterItem[]>;
  filterGroup = new FormGroup({});
  selectedSkillFilters$ = this._selectedSkillFilters$.asObservable();
  showBottomFade$: Rx.Observable<boolean>;
  viewAllFilterControl = new FormControl(true);

  constructor(private datastore: Datastore, private fb: FormBuilder) {}

  ngOnInit() {
    const reviewSkillsCollection = this.datastore.collection<ReviewsSkillsCollection>(
      'reviewsSkills',
      query =>
        query
          .where('toUser', '==', this.usersId$)
          .where('role', '==', this.viewProfileAs$),
    );

    this.reviewSkills$ = reviewSkillsCollection
      .valueChanges()
      .pipe(
        map(reviewsSkills =>
          reviewsSkills.length > 0 ? reviewsSkills[0].skills : [],
        ),
      );

    this.filterControlItems$ = this.reviewSkills$.pipe(
      map(skills =>
        skills.map(skill => ({
          displayName: skill.name,
          controlName: this.sanitiseSkillName(skill.name),
        })),
      ),
    );

    this.filterGroupSubscription = this.filterGroup.valueChanges.subscribe(
      () => {
        this.handleSelectFilterItem();
      },
    );

    this.filterGroupSetupSubscription = this.filterControlItems$
      .pipe(
        tap(filterControlItems => {
          this.filterGroup.reset();
          filterControlItems.forEach(control =>
            this.filterGroup.addControl(
              control.controlName,
              this.fb.control(undefined),
            ),
          );
        }),
      )
      .subscribe();

    this.showBottomFade$ = this.filterControlItems$.pipe(
      map(items => items.length > this.FILTER_CONTROLS_DISPLAY_COUNT),
    );

    this.profileViewAsSubscription = this.viewProfileAs$
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.viewAllFilterControl.setValue(true);
      });
  }

  trackBySkillFilterItem(index: number, skillFilterItem: SkillFilterItem) {
    return skillFilterItem.displayName;
  }

  handleSelectFilterItem(): void {
    this.reviewSkills$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        const selectedSkills = skills.filter(
          skill =>
            this.filterGroup.get(this.sanitiseSkillName(skill.name))?.value,
        );

        if (selectedSkills.length > 0) {
          this.viewAllFilterControl.setValue(false);
        }

        this._selectedSkillFilters$.next(selectedSkills);
        this.filtersSelected.emit(selectedSkills);
      });
  }

  handleResetFilters(): void {
    this.filterGroup.reset();
    this.filtersSelected.emit([]);
    this.viewAllFilterControl.setValue(true);
  }

  sanitiseSkillName(rawName: string): string {
    // An error occurs if a string has a period in it. For example:
    // 'salesforce.com'. So remove all periods from skill names here.
    return rawName.replace('.', '');
  }

  ngOnDestroy() {
    if (this.filterGroupSubscription) {
      this.filterGroupSubscription.unsubscribe();
    }

    if (this.profileViewAsSubscription) {
      this.profileViewAsSubscription.unsubscribe();
    }

    if (this.filterGroupSetupSubscription) {
      this.filterGroupSetupSubscription.unsubscribe();
    }
  }
}
